import React, { FC, useReducer, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Button, CircularProgress, Grid, styled } from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { CheckedImage } from 'assets/images';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import ListItemText from '@mui/material/ListItemText';

import { reportCreatorContent } from 'app/creators/store/creators.actions';
import {
  creatorRollReportErrorSelector,
  creatorRollReportPendingSelector,
} from 'app/creators/store/creators.selectors';

import AppBackdrop from 'components/app-backdrop';
import ModalStayTuned from 'components/modal-stay-tuned';

import useWindowDimensions from 'hooks/use-window-dimensions';
import ModalReportSuccess from './modal-report-success';
import { SCREEN_SIZE_BORDER } from 'constants/frontend-contsants';
import { colors } from 'constants/colors';

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  zIndex: 10000,
  maxHeight: '80dvh',

  '& .MuiDrawer-paper': {
    maxWidth: '100%',
    width: '100%',
    borderRadius: '30px 30px 0 0',
    border: 'none',
    boxShadow: 'none',
    height: '90dvh',
    backgroundColor: colors.default.info,

    [theme.breakpoints.up('md')]: {
      backgroundColor: colors.default.background,
      height: '100dvh',
      borderRadius: 'unset',
    },
  },
}));

const reportList: string[] = [
  'dangerous_organizations_and_individuals',
  'illegal_activities_and_regulated_goods',
  'frauds_and_scams',
  'violent_and_graphic_content',
  'animal_cruelty',
  'suicide_self_harm_and_dangerous_acts',
  'hate_speech',
  'harassment_or_bullying',
  'pornography_and_nudity',
  'minor_safety',
  'spam',
];

interface IReportDrawer {
  open: boolean;
  asset: { id: string; rollId: string; contentId: string };
  onClose: () => any;
}

enum ReportDrawerPage {
  Default = 'default',
  Report = 'report',
  Sucess = 'success',
}

const initialState = {
  page: ReportDrawerPage.Default,
  reason: '',
};

enum DrawerAction {
  NavigateReport,
  NavigateDefault,
  NavigateSuccess,
  Reset,
}

const MainReducer = (
  state: typeof initialState,
  action: { type: DrawerAction; payload: any }
) => {
  switch (action.type) {
    case DrawerAction.NavigateDefault:
      state.page = ReportDrawerPage.Default;
      state.reason = '';
      return { ...state };
    case DrawerAction.NavigateReport:
      state.page = ReportDrawerPage.Report;
      state.reason = action.payload;
      return { ...state };
    case DrawerAction.NavigateSuccess:
      state.page = ReportDrawerPage.Sucess;
      return { ...state };
    case DrawerAction.Reset:
      state.page = ReportDrawerPage.Default;
      state.reason = '';
      return { ...state };
    default:
      return state;
  }
};

// TODO hide alert on navigate
const ReportDrawer: FC<IReportDrawer> = ({ open, asset, onClose }) => {
  const dispatch_main = useDispatch();
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(MainReducer, initialState);
  const { width } = useWindowDimensions();

  const pending_report = useSelector(creatorRollReportPendingSelector);
  const error_report = useSelector(creatorRollReportErrorSelector);

  const handleNavigateBack = () =>
    dispatch({ type: DrawerAction.NavigateDefault, payload: null });
  const handleNavigateReport = (report: string) => () =>
    dispatch({ type: DrawerAction.NavigateReport, payload: report });

  const handleSubmitReport = useCallback(async () => {
    if (!asset) {
      return;
    }

    const { error } = await dispatch_main<any>(
      reportCreatorContent({
        content_id: asset.contentId,
        roll_id: asset.rollId,
        reason: state.reason,
      })
    );

    if (error) {
      return;
    }

    dispatch({ type: DrawerAction.NavigateSuccess, payload: null });
  }, [state, asset, dispatch_main]);

  const handleDoneReport = useCallback(() => {
    dispatch({ type: DrawerAction.Reset, payload: null });
    onClose();
  }, [onClose]);

  const handleCloseReport = useCallback(() => {
    dispatch({ type: DrawerAction.Reset, payload: null });
    onClose();
  }, [onClose]);

  return (
    <>
      <StyledDrawer open={open} onClose={handleCloseReport} anchor="bottom">
        {width >= SCREEN_SIZE_BORDER && (
          <Grid
            data-testid="desktop-drawer"
            sx={{
              backgroundColor: { md: colors.default.background },
              padding: '75px 66px 0',
              display: 'flex',
              flexWrap: 'nowrap',
            }}
            container
          >
            <Grid
              item
              sx={{
                borderRight: '2px solid #303030',
                height: '100dvh',
                flex: '1 1 390px',
                maxWidth: '400px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  padding: '12px',
                  borderBottom: '2px solid #303030',
                }}
              >
                <IconButton
                  onClick={onClose}
                  edge="start"
                  sx={{ padding: '2px' }}
                >
                  <CloseIcon fontSize="small" sx={{ color: colors.default.info }} />
                </IconButton>
                <Box sx={{ ml: '21px' }}>
                  <Typography
                    textTransform={'uppercase'}
                    sx={{
                      color: colors.default.info,
                      fontFamily: 'Avenir',
                      fontSize: '18px',
                    }}
                    component="h3"
                  >
                    {t('titles.report')}
                  </Typography>
                  <Typography
                    sx={{ color: '#515151', fontSize: '12px' }}
                    component="h4"
                  >
                    {t('titles.select-reason')}
                  </Typography>
                </Box>
              </Box>
              <List>
                {reportList.map((code) => (
                  <ListItemButton
                    key={code}
                    sx={{
                      color: colors.default.info,
                      borderBottom: '2px solid #303030',
                    }}
                    selected={state.reason === code}
                    onClick={handleNavigateReport(code)}
                  >
                    <ListItemText
                      primary={t(`lists.${code}`)}
                      sx={{ fontSize: '16px' }}
                    />
                    <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                      <ArrowForwardIosIcon
                        sx={{ fontSize: '16px', color: colors.default.info }}
                      />
                    </ListItemIcon>
                  </ListItemButton>
                ))}
              </List>
            </Grid>
            <Grid
              item
              sx={{
                borderRight: '2px solid #303030',
                flex: '1 1 600px',
                maxWidth: '600px',
              }}
            >
              {state.page === ReportDrawerPage.Report && (
                <>
                  <Box
                    sx={{
                      borderBottom: '2px solid #303030',
                      padding: '17px 10px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <IconButton onClick={handleNavigateBack}>
                      <ArrowBackIosIcon
                        fontSize="small"
                        sx={{ fontSize: '16px', color: colors.default.info }}
                      />
                    </IconButton>
                    <Typography sx={{ color: colors.default.info }} component="h3">
                      {t(`lists.${state.reason}`)}
                    </Typography>
                  </Box>
                  <Box sx={{ padding: '15px' }}>
                    {state.reason && error_report && (
                      <>
                        <ModalStayTuned opened={true} />
                      </>
                    )}
                    <Typography sx={{ fontSize: '14px', color: colors.default.info }}>
                      {t('texts.report-info')}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ width: '60%', marginTop: '20px' }}
                      onClick={handleSubmitReport}
                    >
                      {pending_report ? (
                        <CircularProgress size={24} />
                      ) : (
                        t('buttons.submit')
                      )}
                    </Button>
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
        )}
        {width <= SCREEN_SIZE_BORDER && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px',
                borderBottom: '1px solid #EBEBEB',
                background: '#FFF',
              }}
            >
              <IconButton onClick={handleNavigateBack}>
                {state.page === ReportDrawerPage.Report && (
                  <ArrowBackIosIcon
                    fontSize="small"
                    sx={{ fontSize: '16px', color: '#201523' }}
                  />
                )}
              </IconButton>
              <Typography component="h3">{t('titles.report')}</Typography>
              <IconButton onClick={onClose} sx={{ color: colors.default.black }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            {state.reason && error_report && <ModalStayTuned opened={true} />}
            {pending_report && (
              <AppBackdrop
                styles={{ flex: '1 1', color: colors.default.black }}
                color="inherit"
              />
            )}
            {!pending_report && state.page === ReportDrawerPage.Default && (
              <List
                subheader={
                  <Typography sx={{ color: colors.default.subtitleGrey, fontSize: '12px' }}>
                    {t('titles.select-reason')}
                  </Typography>
                }
                sx={{ padding: '8px 8px 33px' }}
              >
                {reportList.map((code) => (
                  <ListItemButton
                    key={code}
                    sx={{ padding: '8px 0' }}
                    onClick={handleNavigateReport(code)}
                  >
                    <ListItemText
                      primary={t(`lists.${code}`)}
                      sx={{ fontSize: '16px' }}
                    />
                    <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                      <ArrowForwardIosIcon
                        sx={{
                          fontSize: '16px',
                          color: { xs: colors.default.subtitleGrey, md: colors.default.info },
                        }}
                      />
                    </ListItemIcon>
                  </ListItemButton>
                ))}
              </List>
            )}

            {!pending_report && state.page === ReportDrawerPage.Report && (
              <Grid
                sx={{
                  padding: '19px',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: '1 1',
                }}
              >
                <Typography sx={{ fontSize: '14px' }}>
                  {t('texts.report-info')}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ width: '100%', marginTop: 'auto' }}
                  onClick={handleSubmitReport}
                >
                  {pending_report ? (
                    <CircularProgress size={24} />
                  ) : (
                    t('buttons.submit')
                  )}
                </Button>
              </Grid>
            )}

            {!pending_report && state.page === ReportDrawerPage.Sucess && (
              <Grid
                sx={{
                  padding: '19px',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: '1 1',
                }}
              >
                <Grid
                  sx={{
                    display: 'flex',
                    flex: '1 1',
                    textAlign: 'center',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <CheckedImage style={{ marginBottom: '15px' }} />
                  <Typography sx={{ fontSize: '18px' }} component="h3">
                    {t('titles.thanks-for-reporting')}
                  </Typography>
                  <Typography sx={{ fontSize: '14px' }} component="p">
                    {t('texts.thanks-for-reporting')}
                  </Typography>
                </Grid>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ width: '100%' }}
                  onClick={handleDoneReport}
                >
                  {t('buttons.done')}
                </Button>
              </Grid>
            )}
          </>
        )}
      </StyledDrawer>
      <ModalReportSuccess
        open={
          width > SCREEN_SIZE_BORDER && state.page === ReportDrawerPage.Sucess
        }
        title={t('titles.thanks-for-reporting')}
        content={t('texts.thanks-for-reporting')}
        confirmText={t('buttons.done')}
        onConfirm={() => handleDoneReport()}
      />
    </>
  );
};

export default ReportDrawer;
